import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, UserPlus, Zap, ShieldCheck, HelpCircle, TrendingUp, Users, Target, Rocket } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Guia IPTV para Novos Revendedores", href: "/blog/guia-iptv-novos-revendedores" }
  ];

  const sections = [
    {
      title: "1. O Primeiro Passo: Mentalidade de Negócio",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Para quem está começando agora no mundo do IPTV, o primeiro e mais importante passo é entender que este não é um "bico" ou uma forma de ganhar dinheiro fácil sem esforço. É um negócio real, que exige dedicação, organização e, acima de tudo, um foco inabalável na satisfação do cliente.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Diferente de outros modelos de negócio, a revenda de IPTV depende da recorrência. Se você vende uma vez e o cliente não gosta do serviço ou do atendimento, você perdeu um faturamento futuro. Por isso, a mentalidade de um revendedor de elite é: atrair com qualidade e manter com excelência.
          </p>
        </div>
      )
    },
    {
      title: "2. Escolhendo seu Parceiro Tecnológico",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <p className="text-muted-foreground text-center max-w-2xl mx-auto">
            A escolha do servidor (o painel que você vai usar) é a decisão mais crítica da sua jornada. Um erro aqui pode arruinar sua reputação antes mesmo de você começar.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-black/40 p-6 rounded-2xl border border-white/5 space-y-3">
              <ShieldCheck className="w-8 h-8 text-electric" />
              <h4 className="text-white font-bold">Estabilidade Total</h4>
              <p className="text-sm text-muted-foreground">Procure por servidores que utilizam tecnologia P2P e redundância de servidores para evitar travamentos em horários de pico.</p>
            </div>
            <div className="bg-black/40 p-6 rounded-2xl border border-white/5 space-y-3">
              <Zap className="w-8 h-8 text-amber-500" />
              <h4 className="text-white font-bold">Suporte Ativo</h4>
              <p className="text-sm text-muted-foreground">Você precisará de ajuda técnica no início. Tenha certeza de que seu fornecedor oferece suporte rápido e em português.</p>
            </div>
            <div className="bg-black/40 p-6 rounded-2xl border border-white/5 space-y-3">
              <Rocket className="w-8 h-8 text-purple-500" />
              <h4 className="text-white font-bold">Apps Próprios</h4>
              <p className="text-sm text-muted-foreground">Ter aplicativos como o P2 PRO facilita a vida do seu cliente e profissionaliza sua marca imediatamente.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Como Conseguir Seus Primeiros 10 Clientes",
      content: (
        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-muted-foreground">O início pode parecer desafiador, mas a estratégia para os primeiros clientes é simples e baseada em confiança:</p>
          <div className="space-y-4">
            <div className="bg-white/5 p-6 rounded-xl border border-white/10">
              <h4 className="text-white font-bold mb-2 flex items-center gap-2"><Check className="text-electric w-5 h-5" /> Círculo de Confiança</h4>
              <p className="text-muted-foreground">Amigos e familiares são seus primeiros testadores. Ofereça um valor promocional para que eles validem o serviço e ajudem a espalhar a notícia.</p>
            </div>
            <div className="bg-white/5 p-6 rounded-xl border border-white/10">
              <h4 className="text-white font-bold mb-2 flex items-center gap-2"><Check className="text-electric w-5 h-5" /> Demonstração Grátis</h4>
              <p className="text-muted-foreground">O teste de 2 a 6 horas é sua melhor ferramenta de venda. Deixe o produto falar por si só. Se o conteúdo for bom e não travar, a venda é quase certa.</p>
            </div>
            <div className="bg-white/5 p-6 rounded-xl border border-white/10">
              <h4 className="text-white font-bold mb-2 flex items-center gap-2"><Check className="text-electric w-5 h-5" /> Prova Social</h4>
              <p className="text-muted-foreground">Sempre que um cliente elogiar, peça permissão para printar e usar como depoimento (escondendo os dados dele). Isso gera autoridade para novos interessados.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Erros Fatais que Você Deve Evitar",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Muitos iniciantes desistem nos primeiros meses por erros básicos. Anote para não cometer:</p>
          <ul className="space-y-4 list-none p-0">
            <li className="flex gap-3 bg-red-500/5 p-4 rounded-lg border border-red-500/10">
              <span className="text-red-500 font-bold">✘</span>
              <p className="text-sm"><strong className="text-white">Prometer o impossível:</strong> Nunca diga que o IPTV nunca, jamais terá uma oscilação. Seja honesto sobre como a internet do cliente influencia no serviço.</p>
            </li>
            <li className="flex gap-3 bg-red-500/5 p-4 rounded-lg border border-red-500/10">
              <span className="text-red-500 font-bold">✘</span>
              <p className="text-sm"><strong className="text-white">Demorar no atendimento:</strong> No mundo digital, 30 minutos de espera parecem uma eternidade. Responda rápido, mesmo que seja para dizer que está verificando.</p>
            </li>
            <li className="flex gap-3 bg-red-500/5 p-4 rounded-lg border border-red-500/10">
              <span className="text-red-500 font-bold">✘</span>
              <p className="text-sm"><strong className="text-white">Misturar dinheiro:</strong> O que entra da revenda deve ser reinvestido ou guardado. Não gaste seu lucro inicial com futilidades se quiser escalar.</p>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="Guia IPTV para Novos Revendedores: O Manual do Sucesso"
        subtitle="Inicie sua jornada no mercado de entretenimento digital com o pé direito. Aprenda as bases para construir uma revenda sólida e lucrativa."
        heroImage="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Seja bem-vindo ao mercado que mais cresce no Brasil. A revenda de IPTV tornou-se a principal fonte de renda para milhares de brasileiros que buscavam autonomia e lucratividade. No entanto, com o aumento da demanda, o mercado também se tornou mais exigente. Não há mais espaço para amadores.",
          "Este guia foi escrito especificamente para você, que está dando os primeiros passos ou que acabou de adquirir seu primeiro painel. Vamos focar no que realmente importa: como escolher seu fornecedor, como atrair seus primeiros clientes de forma ética e como organizar seu dia a dia para que o negócio cresça sem tomar todo o seu tempo.",
          "Prepare-se para transformar seu celular e seu computador em uma verdadeira central de lucros. Vamos construir sua base agora."
        ]}
        breadcrumbs={breadcrumbs}
        faq={[
          {
            question: "Preciso de muito dinheiro para começar?",
            answer: "Não. O modelo de revenda IPTV é um dos que exige o menor capital inicial. Geralmente, você começa adquirindo um pacote de créditos ou pagando uma pequena taxa de manutenção do painel."
          },
          {
            question: "Como lidar com clientes que têm internet ruim?",
            answer: "A honestidade é a chave. Explique que o IPTV de qualidade exige estabilidade. Recomende o uso de cabo de rede em vez do Wi-Fi sempre que possível."
          }
        ]}
        benefits={[
          {
            title: "Trabalhe de Casa",
            description: "Gerencie toda a sua operação de qualquer lugar do mundo, apenas com acesso à internet.",
            icon: <Users className="w-6 h-6" />
          },
          {
            title: "Lucro Imediato",
            description: "Diferente de outros negócios, na revenda IPTV o retorno sobre o investimento costuma vir já no primeiro mês.",
            icon: <TrendingUp className="w-6 h-6" />
          }
        ]}
        howItWorks={[
          {
            title: "Estudo e Preparação",
            description: "Conheça o painel, teste os canais e entenda como funciona a instalação nos dispositivos."
          },
          {
            title: "Prospecção Ativa",
            description: "Divulgue nos seus círculos e redes sociais focando nos benefícios e na economia para o cliente."
          }
        ]}
        diferenciais={[
          {
            title: "Mentoria e Suporte",
            description: "Aqui na P2 PRO, não apenas entregamos o painel, mas ensinamos você a vender.",
            icon: <HelpCircle className="w-6 h-6" />
          }
        ]}
        sections={sections}
        ctaTitle="🎯 Quer Começar com o Pé Direito?"
        ctaDescription="Junte-se ao time de revendedores que mais cresce no país. Ofereça tecnologia P2P e garanta a satisfação total dos seus clientes desde o primeiro dia."
        ctaButtonText="Quero me Tornar um Revendedor de Elite"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/guia-iptv-novos-revendedores")({
  head: () => ({
    meta: [
      { title: "Guia IPTV para Novos Revendedores: O Manual do Sucesso" },
      { name: "description", content: "Passo a passo completo para quem está começando na revenda IPTV. Evite erros comuns, aprenda a captar clientes e escolha o melhor painel para lucrar." },
    ],
  }),
  component: ArticlePage,
});